import React, { useEffect, useState } from 'react';
import { Platform, Share, Switch, Text, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import { showError, showSuccess } from '../toast';
import { api } from '../api';
import { BIOMETRIC_KEY, useAuth } from '../AuthContext';
import { LegalLinks } from '../legal';
import { Badge, Button, Card, Chip, COLORS, Input, Label, Muted, Row, Screen, Title } from '../ui';
import { getLocaleChoice, LocaleChoice, setLocaleChoice, t } from '../i18n';
import { joinRegions, parseRegions, RegionMultiPicker } from '../settlement-picker';

// Функция, а не константа — етикетите се превеждат при рендиране (смяна на езика)
const roleLabels = (): Record<string, string> => ({
  ADMIN: t('Админ'),
  MODERATOR: t('Модератор'),
  LANDLORD: t('Наемодател'),
  AGENCY: t('Пропърти мениджър'),
  OWNER: t('Собственик'),
  TENANT: t('Наемател'),
  HANDYMAN: t('Майстор'),
  CREATOR: t('Криейтър'),
  RESIDENT: t('Обитател'),
});

export default function ProfileScreen({ navigation }: any) {
  const { user, signOut } = useAuth();
  const [iban, setIban] = useState('');
  const [saving, setSaving] = useState(false);
  const canEditIban = user?.role === 'LANDLORD' || user?.role === 'AGENCY' || user?.role === 'OWNER';
  const isTenant = user?.role === 'TENANT';
  const isIssuer = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const isHandyman = user?.role === 'HANDYMAN';

  // Език: изричен избор (auto / bg / en). При смяна App.tsx монтира дървото наново.
  const [localeChoice, setLocaleChoiceState] = useState<LocaleChoice>(getLocaleChoice());
  const chooseLocale = async (next: LocaleChoice) => {
    setLocaleChoiceState(next);
    await setLocaleChoice(next);
  };

  // Майстор: области, в които работи (workArea е текстът им за съвместимост)
  const [workRegions, setWorkRegions] = useState<string[]>([]);

  // Наемател: ЕГН + адрес (за договори/фактури)
  const [tenantEgn, setTenantEgn] = useState('');
  const [idCardAddress, setIdCardAddress] = useState('');
  const [hasIdData, setHasIdData] = useState(true);

  // Лична карта — влиза в договора за двете страни (№, издадена на, РПУ)
  const [middleName, setMiddleName] = useState('');
  const [idCardNumber, setIdCardNumber] = useState('');
  const [idCardIssuedAt, setIdCardIssuedAt] = useState('');
  const [idCardIssuedBy, setIdCardIssuedBy] = useState('');

  // Издател: реквизити за фактури
  const [inv, setInv] = useState({
    invoiceCity: '',
    invoiceAddress: '',
    mol: '',
    bankName: '',
    bic: '',
    vatRegistered: false,
    vatExemptArt45: false,
  });

  // Вход и сигурност: парола (вход без SMS) + биометрия
  const [newPassword, setNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [bioSupported, setBioSupported] = useState(false);
  const [bioEnabled, setBioEnabled] = useState(false);

  // Моите данни: износ (GDPR чл. 20) и изтриване на акаунта (чл. 17)
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [blockers, setBlockers] = useState<string[] | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const data = await api('/me/profile/export');
      const json = JSON.stringify(data, null, 2);
      if (Platform.OS === 'web') {
        const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'livupp-moite-danni.json';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        await Share.share({ message: json, title: t('Моите данни в Livupp') });
      }
    } catch (e: any) {
      showError(e.message);
    } finally {
      setExporting(false);
    }
  };

  // Първо показваме какво пречи, после искаме парола — без изненади
  const openDelete = async () => {
    setDeleteOpen(true);
    try {
      const r = await api('/me/profile/deletion-check');
      setBlockers(r.blockers);
    } catch (e: any) {
      showError(e.message);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api('/me/profile/delete-account', { body: { password: deletePassword } });
      showSuccess(t('Акаунтът е изтрит. Личните ви данни са заличени.'));
      await signOut();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (Platform.OS !== 'web') {
        const hardware = await LocalAuthentication.hasHardwareAsync();
        const enrolled = await LocalAuthentication.isEnrolledAsync();
        setBioSupported(hardware && enrolled);
      }
      setBioEnabled((await AsyncStorage.getItem(BIOMETRIC_KEY)) === '1');
    })();
  }, []);

  const savePassword = async () => {
    setSavingPassword(true);
    try {
      await api('/auth/set-password', { body: { password: newPassword } });
      setNewPassword('');
      showSuccess(t('Паролата е запазена — вече влизате без SMS, с телефон или имейл.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleBiometric = async (value: boolean) => {
    if (value) {
      const result = await LocalAuthentication.authenticateAsync({ promptMessage: t('Потвърдете, за да включите') });
      if (!result.success) return;
      await AsyncStorage.setItem(BIOMETRIC_KEY, '1');
      setBioEnabled(true);
      showSuccess(t('Включено — при отваряне Livupp ще иска лицево разпознаване/отпечатък.'));
    } else {
      await AsyncStorage.removeItem(BIOMETRIC_KEY);
      setBioEnabled(false);
    }
  };

  useEffect(() => {
    api('/me/profile')
      .then((r) => {
        const profile = r.user.landlordProfile ?? r.user.agencyProfile ?? r.user.ownerProfile;
        if (profile?.iban) setIban(profile.iban);
        if (profile) {
          setInv({
            invoiceCity: profile.invoiceCity ?? '',
            invoiceAddress: profile.invoiceAddress ?? '',
            mol: profile.mol ?? '',
            bankName: profile.bankName ?? '',
            bic: profile.bic ?? '',
            vatRegistered: !!profile.vatRegistered,
            vatExemptArt45: !!profile.vatExemptArt45,
          });
        }
        setMiddleName(r.user.middleName ?? '');
        const idSrc = r.user.tenantProfile ?? r.user.landlordProfile;
        if (idSrc) {
          setIdCardNumber(idSrc.idCardNumber ?? '');
          setIdCardIssuedAt(idSrc.idCardIssuedAt ? new Date(idSrc.idCardIssuedAt).toLocaleDateString('bg-BG') : '');
          setIdCardIssuedBy(idSrc.idCardIssuedBy ?? '');
        }
        const tp = r.user.tenantProfile;
        if (tp) {
          setTenantEgn(tp.egn ?? '');
          setIdCardAddress(tp.idCardAddress ?? '');
          setHasIdData(!!tp.hasIdData);
        }
        if (r.user.landlordProfile?.idCardAddress) setIdCardAddress(r.user.landlordProfile.idCardAddress);
        const hp = r.user.handymanProfile;
        if (hp) setWorkRegions(hp.workRegions?.length ? hp.workRegions : parseRegions(hp.workArea));
      })
      .catch(() => {});
  }, []);

  // „дд.мм.гггг“ → ISO дата (null при невалиден вход)
  const parseBgDate = (v: string): string | null => {
    const m = v.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!m) return null;
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    return d.getDate() === Number(m[1]) ? d.toISOString() : null;
  };

  const idCardBody = () => ({
    middleName,
    idCardNumber,
    idCardIssuedBy,
    ...(parseBgDate(idCardIssuedAt) ? { idCardIssuedAt: parseBgDate(idCardIssuedAt) } : {}),
  });

  // Наемодател: само данните от личната карта (за договора)
  const saveLandlordId = async () => {
    if (idCardIssuedAt && !parseBgDate(idCardIssuedAt)) {
      showError(t('Невалидна дата. Използвайте дд.мм.гггг.'));
      return;
    }
    setSaving(true);
    try {
      await api('/me/profile', { method: 'PATCH', body: { ...idCardBody(), idCardAddress } });
      showSuccess(t('Данните са запазени.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveTenantId = async () => {
    setSaving(true);
    try {
      if (idCardIssuedAt && !parseBgDate(idCardIssuedAt)) {
        showError(t('Невалидна дата. Използвайте дд.мм.гггг.'));
        return;
      }
      await api('/me/profile', { method: 'PATCH', body: { tenantEgn, idCardAddress, ...idCardBody() } });
      setHasIdData(!!tenantEgn && !!idCardAddress);
      showSuccess(t('Данните са запазени.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveInvoiceDetails = async () => {
    setSaving(true);
    try {
      await api('/me/profile', { method: 'PATCH', body: inv });
      showSuccess(t('Реквизитите за фактури са запазени.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveIban = async () => {
    setSaving(true);
    try {
      await api('/me/profile', { method: 'PATCH', body: { iban } });
      showSuccess(t('IBAN-ът е запазен.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Майстор: областите → workRegions + workArea (текст, за стари клиенти)
  const saveWorkRegions = async () => {
    setSaving(true);
    try {
      await api('/me/profile', { method: 'PATCH', body: { workRegions, workArea: joinRegions(workRegions) } });
      showSuccess(t('Областите са запазени.'));
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Профил')}</Title>
      <Card>
        <Row left={t('Име')} right={`${user?.firstName} ${user?.lastName}`} />
        <Row left={t('Телефон')} right={user?.phone ?? ''} />
        <Row left={t('Роля')} right={roleLabels()[user?.role ?? ''] ?? ''} />
      </Card>

      <Card>
        <Label>{t('Език')}</Label>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {(
            [
              ['auto', t('Автоматично')],
              ['bg', 'Български'],
              ['en', 'English'],
            ] as const
          ).map(([code, label]) => (
            <Chip key={code} text={label} active={localeChoice === code} onPress={() => void chooseLocale(code)} />
          ))}
        </View>
        <Muted style={{ marginTop: 8 }}>{t('Автоматично: според телефона и местоположението.')}</Muted>
      </Card>

      {canEditIban && (
        <Card>
          <Label>{t('IBAN за получаване на наема')}</Label>
          <Input value={iban} onChangeText={setIban} autoCapitalize="characters" placeholder="BG80BNBG96611020345678" />
          <Muted>{t('Видим е за наемателите ви при плащане по банка.')}</Muted>
          <Button title={t('Запази IBAN')} onPress={saveIban} disabled={!iban} loading={saving} />
        </Card>
      )}

      {isHandyman && (
        <Card>
          <Label>{t('Области, в които работите')}</Label>
          <RegionMultiPicker values={workRegions} onChange={setWorkRegions} />
          <Muted>{t('Получавате запитвания от всички населени места в избраните области.')}</Muted>
          <Button title={t('Запази областите')} onPress={saveWorkRegions} disabled={workRegions.length === 0} loading={saving} />
        </Card>
      )}

      {isTenant && (
        <Card style={!hasIdData ? { borderColor: COLORS.danger, borderWidth: 2 } : {}}>
          <Label>{t('Данни за договори и фактури')}</Label>
          {!hasIdData && <Badge text={t('Липсват данни — наемодателят не може да ви издаде документи')} color={COLORS.danger} />}
          <Label>{t('ЕГН')}</Label>
          <Input value={tenantEgn} onChangeText={setTenantEgn} keyboardType="number-pad" maxLength={10} placeholder="7523169263" />
          <Label>{t('Адрес по лична карта')}</Label>
          <Input value={idCardAddress} onChangeText={setIdCardAddress} placeholder={t('гр. …, ул. …, №')} />
          <Label>{t('Презиме')}</Label>
          <Input value={middleName} onChangeText={setMiddleName} placeholder={t('за пълното име в договора')} />
          <Label>{t('Лична карта №')}</Label>
          <Input value={idCardNumber} onChangeText={setIdCardNumber} keyboardType="number-pad" placeholder="123456789" />
          <Label>{t('Издадена на')}</Label>
          <Input value={idCardIssuedAt} onChangeText={setIdCardIssuedAt} placeholder={t('дд.мм.гггг')} />
          <Label>{t('От РПУ на МВР — град')}</Label>
          <Input value={idCardIssuedBy} onChangeText={setIdCardIssuedBy} placeholder={t('напр. София')} />
          <Muted>{t('ЕГН се пази криптирано и се вижда само от наемодателя ви. Данните от личната карта влизат в договора.')}</Muted>
          <Button title={t('Запази')} onPress={saveTenantId} disabled={!tenantEgn || !idCardAddress} loading={saving} />
        </Card>
      )}

      {user?.role === 'LANDLORD' && (
        <Card>
          <Label>{t('Мои данни за договора')}</Label>
          <Muted>{t('Влизат в договора за наем на мястото на вашите данни. Попълват се веднъж.')}</Muted>
          <Label>{t('Презиме')}</Label>
          <Input value={middleName} onChangeText={setMiddleName} placeholder={t('за пълното име в договора')} />
          <Label>{t('Лична карта №')}</Label>
          <Input value={idCardNumber} onChangeText={setIdCardNumber} keyboardType="number-pad" placeholder="123456789" />
          <Label>{t('Издадена на')}</Label>
          <Input value={idCardIssuedAt} onChangeText={setIdCardIssuedAt} placeholder={t('дд.мм.гггг')} />
          <Label>{t('От РПУ на МВР — град')}</Label>
          <Input value={idCardIssuedBy} onChangeText={setIdCardIssuedBy} placeholder={t('напр. Пловдив')} />
          <Label>{t('Адрес')}</Label>
          <Input value={idCardAddress} onChangeText={setIdCardAddress} placeholder={t('гр. …, ул. …, №')} />
          <Button title={t('Запази')} onPress={saveLandlordId} loading={saving} />
        </Card>
      )}

      {isIssuer && (
        <Card>
          <Label>{t('Данни за фактури')}</Label>
          <Label>{t('Град')}</Label>
          <Input value={inv.invoiceCity} onChangeText={(t) => setInv({ ...inv, invoiceCity: t })} placeholder={t('София')} />
          <Label>{t('Адрес')}</Label>
          <Input value={inv.invoiceAddress} onChangeText={(t) => setInv({ ...inv, invoiceAddress: t })} placeholder={t('ул. …, №')} />
          <Label>{t('МОЛ (за фирма)')}</Label>
          <Input value={inv.mol} onChangeText={(t) => setInv({ ...inv, mol: t })} placeholder={t('Иван Иванов')} />
          <Label>{t('Банка')}</Label>
          <Input value={inv.bankName} onChangeText={(t) => setInv({ ...inv, bankName: t })} placeholder={t('ДСК')} />
          <Label>BIC</Label>
          <Input value={inv.bic} onChangeText={(t) => setInv({ ...inv, bic: t })} autoCapitalize="characters" placeholder="STSABGSF" />
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 12 }}>
            <Switch value={inv.vatRegistered} onValueChange={(v) => setInv({ ...inv, vatRegistered: v })} />
            <Text style={{ marginLeft: 10, flex: 1, color: COLORS.text }}>{t('Регистриран по ЗДДС')}</Text>
          </View>
          {inv.vatRegistered && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
              <Switch value={inv.vatExemptArt45} onValueChange={(v) => setInv({ ...inv, vatExemptArt45: v })} />
              <Text style={{ marginLeft: 10, flex: 1, color: COLORS.text }}>
                {t('Освободена доставка (чл. 45 ЗДДС — жилище на физ. лице)')}
              </Text>
            </View>
          )}
          <Muted>
            {t('ДДС режимът определя как се начислява данъкът във фактурите. При съмнение се консултирайте със счетоводител — приложението не взима данъчни решения вместо вас.')}
          </Muted>
          <Button title={t('Запази реквизитите')} onPress={saveInvoiceDetails} loading={saving} />
        </Card>
      )}

      <Card>
        <Label>{t('Вход и сигурност')}</Label>
        <Muted>{t('Задайте парола, за да влизате без SMS код — с телефон или имейл.')}</Muted>
        <Input
          value={newPassword}
          onChangeText={setNewPassword}
          secureTextEntry
          placeholder={t('Нова парола (мин. 8 знака)')}
          style={{ marginTop: 8 }}
        />
        <Button
          title={t('Запази паролата')}
          variant="secondary"
          onPress={savePassword}
          disabled={newPassword.length < 8}
          loading={savingPassword}
        />

        {bioSupported ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
            <Switch value={bioEnabled} onValueChange={toggleBiometric} />
            <Text style={{ marginLeft: 10, flex: 1, color: COLORS.text }}>
              {t('Изисквай лицево разпознаване / отпечатък при отваряне')}
            </Text>
          </View>
        ) : (
          Platform.OS !== 'web' && (
            <Muted>{t('Лицево разпознаване не е налично на това устройство.')}</Muted>
          )
        )}
      </Card>

      <Card>
        <Label>{t('Моите данни')}</Label>
        <Muted>
          {t('Свалете копие на всичко, което Livupp пази за вас, или изтрийте акаунта си заедно с личните данни.')}
        </Muted>
        <Button title={t('Изтегли моите данни')} variant="secondary" onPress={exportData} loading={exporting} />

        {!deleteOpen ? (
          <Button title={t('Изтриване на акаунта')} variant="secondary" onPress={openDelete} />
        ) : (
          <View style={{ marginTop: 12, borderTopWidth: 1, borderTopColor: COLORS.border, paddingTop: 12 }}>
            {blockers === null ? (
              <Muted>{t('Проверяваме дали имате незавършени ангажименти…')}</Muted>
            ) : blockers.length > 0 ? (
              <>
                <Badge text={t('Акаунтът не може да бъде изтрит сега')} color={COLORS.danger} />
                {blockers.map((b) => (
                  <Text key={b} style={{ color: COLORS.text, marginTop: 8 }}>
                    • {b}
                  </Text>
                ))}
                <Button title={t('Затвори')} variant="secondary" onPress={() => setDeleteOpen(false)} />
              </>
            ) : (
              <>
                <Text style={{ color: COLORS.danger, fontWeight: '600' }}>{t('Това действие е необратимо.')}</Text>
                <Muted>
                  {t('Името, телефонът, имейлът, ЕГН-то, IBAN-ът и снимките на личната ви карта се заличават завинаги. Договорите, фактурите и плащанията остават анонимизирани — законът изисква да се пазят 10 години (чл. 12 ЗСч). Въведете паролата си, за да потвърдите.')}
                </Muted>
                <Input
                  value={deletePassword}
                  onChangeText={setDeletePassword}
                  secureTextEntry
                  autoCapitalize="none"
                  placeholder={t('Вашата парола')}
                  style={{ marginTop: 8 }}
                />
                <Button
                  title={t('Изтрий акаунта завинаги')}
                  variant="danger"
                  onPress={confirmDelete}
                  disabled={!deletePassword}
                  loading={deleting}
                />
                <Button title={t('Отказ')} variant="secondary" onPress={() => setDeleteOpen(false)} />
              </>
            )}
          </View>
        )}
      </Card>

      <Card>
        <Label>{t('Помощ')}</Label>
        <Muted>{t('Нещо не работи или имате въпрос? Пишете ни — отговаряме бързо.')}</Muted>
        <Button title={t('✉️ Съобщи проблем / поддръжка')} variant="secondary" onPress={() => navigation.navigate('Support')} />
      </Card>

      <Card>
        <Label>{t('Правна информация')}</Label>
        <LegalLinks />
      </Card>

      <Button title={t('Изход')} variant="danger" onPress={signOut} />
    </Screen>
  );
}
